import { useState, useEffect, useCallback } from 'react';
import { useParams, NavLink } from 'react-router-dom';
import { PLATFORM_FEE_PERCENT } from '../types';
import { useTournament, useHasJoined, callJoinTournament, useTournamentEntries, callLeaveTournament, useMySquads } from '../services/tournamentService';
import { useWalletData } from '../services/walletService';
import { useAuth } from '../context/AuthContext';
import { ArrowLeft, Clock, Trophy, Crosshair, AlertTriangle, Copy, Check, Users, ChevronDown, ChevronUp, User, XCircle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useModal } from '../context/ModalContext';
import { useWalletLock } from '../context/WalletLockContext';
import { WildRiftDetail } from './WildRiftDetail';
import TCoinIcon from '../components/TCoinIcon';
import GameRulesCard from '../components/GameRulesCard';
import AvatarDisplay from '../components/AvatarDisplay';

function getTimeLeft(start: string, status?: string) {
    const diff = new Date(start).getTime() - Date.now();
    if (status === 'live') return 'In Progress';
    if (status === 'completed') return 'Ended';
    if (diff <= 0) return 'Starting soon';
    const d = Math.floor(diff / 86400000);
    const h = Math.floor((diff % 86400000) / 3600000);
    const m = Math.floor((diff % 3600000) / 60000);
    if (d > 0) return `${d}d ${h}h`;
    return `${h}h ${m}m`;
}

export default function TournamentDetail() {
    const { showAlert } = useModal();
    const { requireUnlock } = useWalletLock();
    const { user } = useAuth();
    const { id } = useParams();

    // ── Data Sources: mock vs Firestore ──
    const { tournament: realTournament } = useTournament(id);
    const { hasJoined: realHasJoined } = useHasJoined(user?.uid, id);
    const { wallet } = useWalletData(user?.uid);

    const t = realTournament;
    const hasJoined = realHasJoined;
    const walletBalance = wallet.balance;

    const [showModal, setShowModal] = useState(false);
    const [joinState, setJoinState] = useState<'confirm' | 'success' | 'confirm-cancel' | 'canceled'>('confirm');
    const [joining, setJoining] = useState(false);
    const [canceling, setCanceling] = useState(false);

    const [copiedField, setCopiedField] = useState<string | null>(null);
    const [activeTab, setActiveTab] = useState<'info' | 'players' | 'teams'>('info');

    // Live tournament entries for roster display
    const { entries: liveEntries } = useTournamentEntries(id);
    const isSolo = t?.mode === 'Solo';
    const isSquad = t?.mode === 'Squad';



    const handleCopy = (text: string, field: string) => {
        navigator.clipboard.writeText(text).then(() => {
            setCopiedField(field);
            setTimeout(() => setCopiedField(null), 1500);
        });
    };

    const { squads: mySquads } = useMySquads(user?.uid);

    const handleJoinClick = () => {
        if (t && t.mode !== 'Solo') {
            const playerSquad = mySquads.find(s => s.game === t.game);
            if (!playerSquad) {
                showAlert(`You must be in a ${t.game} squad to join a ${t.mode} tournament.\nPlease create a squad in your Profile first.`, 'Squad Required');
                return;
            }
            if (playerSquad.status === 'forming') {
                showAlert("Your squad is not fully formed yet! All members must accept their invitations before you can register for a tournament.", 'Squad Forming');
                return;
            }
            const uid = user?.uid || '';
            const isLeader = playerSquad.members.some(m => m.uid === uid && m.role === 'leader');
            const isCoLeader = playerSquad.members.some(m => m.uid === uid && m.role === 'co-leader');
            if (!isLeader && !isCoLeader) {
                showAlert(`Only the Squad Leader or Co-Leader can join tournaments on behalf of the squad.`, 'Permission Denied');
                return;
            }
        }

        // ── Wallet Lock Gate ──
        requireUnlock(() => {
            setJoinState('confirm');
            setShowModal(true);
        });
    };

    const handleConfirmJoin = async () => {
        if (!t) return;

        if (walletBalance < t.entryFee) {
            showAlert('Insufficient balance to join tournament!', 'Error');
            setShowModal(false);
            return;
        }

        // Live mode: call Cloud Function
        setJoining(true);
        try {
            const squadId = t.mode !== 'Solo' ? mySquads.find(s => s.game === t.game)?.id : undefined;
            const result = await callJoinTournament(t.id, squadId);
            if (result.success) {
                setJoinState('success');
            } else {
                showAlert(result.message || 'Failed to join tournament.', 'Error');
                setShowModal(false);
            }
        } catch (err: unknown) {
            showAlert(err instanceof Error ? err.message : 'Failed to join tournament. Please try again.', 'Error');
            setShowModal(false);
        } finally {
            setJoining(false);
        }
    };

    const handleConfirmCancel = async () => {
        if (!t) return;

        setCanceling(true);
        try {
            const result = await callLeaveTournament(t.id);
            if (result.success) {
                setJoinState('canceled');
            } else {
                showAlert(result.message || 'Failed to cancel registration.', 'Error');
                setShowModal(false);
            }
        } catch (err: unknown) {
            showAlert(err instanceof Error ? err.message : 'Failed to cancel registration.', 'Error');
            setShowModal(false);
        } finally {
            setCanceling(false);
        }
    };

    if (!t) {
        return (
            <div className="page-wrapper" style={{ textAlign: 'center', paddingTop: 80 }}>
                <p style={{ fontSize: '3rem', marginBottom: 16 }}>🔍</p>
                <h2>Tournament Not Found</h2>
                <NavLink to="/tournaments" className="btn btn-secondary" style={{ marginTop: 16, display: 'inline-flex' }}>
                    <ArrowLeft size={16} /> Back to Tournaments
                </NavLink>
            </div>
        );
    }

    const fillPct = Math.round((t.slotFill / t.maxSlots) * 100);

    // MOBA games (Wild Rift, MLBB) get the dedicated MOBA detail page
    if (t.game === 'Wild Rift' || t.game === 'MLBB') {
        return <WildRiftDetail tournament={t} />;
    }

    const currentPool = Math.round(t.slotFill * t.entryFee * (1 - PLATFORM_FEE_PERCENT));
    const estPool = Math.round(t.maxSlots * t.entryFee * (1 - PLATFORM_FEE_PERCENT));
    const MIN_FILL_PERCENT = 60;

    const cutoffHours = 1;
    const msUntilStart = new Date(t.startTime).getTime() - Date.now();
    const canCancel = msUntilStart > cutoffHours * 3600 * 1000;

    return (
        <div className="page-wrapper" style={{ maxWidth: 800 }}>
            <NavLink to="/tournaments" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, color: 'var(--text-muted)', fontSize: '0.88rem', marginBottom: 20 }}>
                <ArrowLeft size={16} /> Back to Tournaments
            </NavLink>

            {/* Header Card */}
            <div className="card animate-in" style={{ marginBottom: 20, position: 'relative', overflow: 'hidden' }}>
                <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 4, background: 'var(--accent-gradient)' }} />
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12, paddingTop: 8 }}>
                    <div>
                        <span className={`tc-game-badge ${t.game === 'PUBG' ? 'pubg' : t.game === 'Free Fire' ? 'freefire' : 'wildrift'}`} style={{ marginBottom: 10, display: 'inline-flex' }}>
                            {t.game} • {t.mode}
                        </span>
                        <h1 style={{ fontFamily: 'Outfit', fontSize: '1.8rem', fontWeight: 800, marginTop: 8 }}>{t.title}</h1>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 }}>
                            <span style={{ fontSize: '0.75rem', fontFamily: 'monospace', background: 'rgba(255,255,255,0.05)', padding: '2px 6px', borderRadius: 4, color: 'var(--text-secondary)', border: '1px solid var(--border)' }}>
                                {t.id}
                            </span>
                            <button onClick={(e) => { e.preventDefault(); handleCopy(t.id, 'tourneyId'); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: copiedField === 'tourneyId' ? 'var(--success)' : 'var(--text-muted)', display: 'flex', alignItems: 'center', padding: 2, transition: 'color 0.2s' }} title="Copy Tournament ID">
                                {copiedField === 'tourneyId' ? <Check size={14} /> : <Copy size={14} />}
                            </button>
                        </div>
                        <p style={{ color: 'var(--text-muted)', marginTop: 6 }}>
                            {new Date(t.startTime).toLocaleString('en-US', { weekday: 'long', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                        </p>
                    </div>
                    <span className={`tc-status ${t.status}`} style={{ fontSize: '0.85rem', padding: '6px 14px', ...(t.status === 'completed' ? { background: 'rgba(245,158,11,0.1)', color: '#f59e0b' } : {}) }}>
                        {t.status === 'live' && <span className="pulse-dot" />}
                        {t.status === 'completed' ? 'Processing Results' : t.status.charAt(0).toUpperCase() + t.status.slice(1)}
                    </span>
                </div>
                <div className="tc-countdown" style={{ fontSize: '0.92rem', marginBottom: 16 }}>
                    <Clock size={16} /> {t.status === 'live' ? 'Match in progress' : `Starts in ${getTimeLeft(t.startTime, t.status)}`}
                </div>

                {/* Registration Countdown Timer */}
                {t.registrationCloseTime && (t.status === 'registering' || t.status === 'open') && (
                    <RegistrationCountdownBanner closeTime={t.registrationCloseTime} />
                )}

                {/* Underfilled Warning */}
                { (t.status === 'registering' || t.status === 'open') && fillPct < MIN_FILL_PERCENT && (
                    <div style={{ padding: '12px 16px', background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.2)', borderRadius: 'var(--radius-md)', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 10 }}>
                        <AlertTriangle size={16} style={{ color: 'var(--warning)', flexShrink: 0 }} />
                        <span style={{ fontSize: '0.85rem', color: 'var(--warning)' }}>This tournament needs at least {Math.ceil(t.maxSlots * MIN_FILL_PERCENT / 100)} players to start. If the threshold isn't met, the match will be placed on hold for admin review.</span>
                    </div>
                )}
            </div>

            {/* Stats Row */}
            <div className="responsive-stats-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 20 }}>
                {[
                    { icon: <Trophy size={18} />, label: 'Current Pool', value: <><TCoinIcon size={18} /> {currentPool.toLocaleString()}</>, color: 'var(--success)' },
                    { icon: <Trophy size={18} />, label: 'Est. Pool', value: <><TCoinIcon size={18} /> {estPool.toLocaleString()}</>, color: 'var(--text-muted)' },
                    { icon: <Crosshair size={18} />, label: 'Per Kill', value: (t.game === 'PUBG' || t.game === 'Free Fire') ? <><TCoinIcon size={18} /> {Math.round(t.entryFee * 0.10)}</> : (t.perKill ? <><TCoinIcon size={18} /> {t.perKill}</> : '—'), color: 'var(--neon-blue)' },
                    { icon: <Clock size={18} />, label: 'Entry Fee', value: <><TCoinIcon size={18} /> {t.entryFee}</>, color: 'var(--warning)' },
                ].map((s, i) => (
                    <div key={i} className={`stat-card animate-in stagger-${i + 1}`} style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', height: '100%' }}>
                        <div style={{ color: s.color, marginBottom: 8 }}>{s.icon}</div>
                        <div className="stat-label">{s.label}</div>
                        <div className="stat-value" style={{ fontSize: '1.3rem', color: s.color, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '4px', marginTop: 'auto' }}>{s.value}</div>
                    </div>
                ))}
            </div>

            {/* Slot Bar */}
            <div className="card animate-in stagger-2" style={{ marginBottom: 20 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                    <span style={{ fontSize: '0.85rem', fontWeight: 600 }}>Slot Fill</span>
                    <span style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>{fillPct}%</span>
                </div>
                <div className="tc-slots-bar" style={{ height: 10, borderRadius: 5 }}>
                    <motion.div className="tc-slots-fill" 
                        initial={{ width: 0 }}
                        animate={{ width: `${fillPct}%` }}
                        transition={{ duration: 1, ease: "easeOut" }}
                        style={{
                            background: fillPct >= 75 ? 'var(--success)' : fillPct >= 50 ? 'var(--warning)' : 'var(--accent)',
                            borderRadius: 5,
                            height: '100%'
                    }} />
                </div>
                <p style={{ marginTop: 6, fontSize: '0.8rem', color: 'var(--text-muted)' }}>{t.maxSlots - t.slotFill} slot{t.maxSlots - t.slotFill !== 1 ? 's' : ''} remaining</p>
            </div>

            {/* ── Tab Bar (BR Games) ── */}
            {(isSolo || isSquad) && (
                <div className="card animate-in stagger-2" style={{ marginBottom: 20, padding: '4px', display: 'flex', gap: 4, borderRadius: 10 }}>
                    <button
                        onClick={() => setActiveTab('info')}
                        style={{
                            flex: 1, padding: '10px 0', borderRadius: 8, border: 'none', cursor: 'pointer',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                            fontSize: '0.85rem', fontWeight: 600, transition: 'all 0.2s',
                            background: activeTab === 'info' ? 'var(--accent)' : 'transparent',
                            color: activeTab === 'info' ? '#fff' : 'var(--text-secondary)',
                        }}
                    >
                        <Trophy size={14} /> Info
                    </button>
                    <button
                        onClick={() => setActiveTab(isSolo ? 'players' : 'teams')}
                        style={{
                            flex: 1, padding: '10px 0', borderRadius: 8, border: 'none', cursor: 'pointer',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                            fontSize: '0.85rem', fontWeight: 600, transition: 'all 0.2s',
                            background: (activeTab === 'players' || activeTab === 'teams') ? 'var(--accent)' : 'transparent',
                            color: (activeTab === 'players' || activeTab === 'teams') ? '#fff' : 'var(--text-secondary)',
                        }}
                    >
                        {isSolo ? <><User size={14} /> Players ({liveEntries.length || t.slotFill})</> : <><Users size={14} /> Teams</>}
                    </button>
                </div>
            )}

            {/* ── Tab Content ── */}
            <AnimatePresence mode="wait">
            {(activeTab === 'info' || (!isSolo && !isSquad)) && (
            <motion.div key="info" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}>

            {/* Room Details (Live Matches) */}
            {Boolean(t.status === 'live' && t.roomId && t.roomPassword) && (
                <div className="card animate-in stagger-3" style={{
                    marginBottom: 20, border: '2px dashed rgba(245,158,11,0.5)',
                    background: 'linear-gradient(135deg, rgba(245,158,11,0.04), rgba(16,185,129,0.04))',
                    padding: '20px 24px',
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
                        <span className="pulse-dot" />
                        <span style={{ fontFamily: 'Outfit', fontWeight: 800, fontSize: '0.85rem', letterSpacing: 1.5, textTransform: 'uppercase', color: 'var(--success)' }}>
                            Match is Live — Room Details
                        </span>
                    </div>
                    <div className="responsive-form-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
                        <div>
                            <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', fontWeight: 600, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 6 }}>Room ID</div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                <span style={{ fontFamily: 'monospace', fontSize: '1.3rem', fontWeight: 800, color: 'var(--success)', letterSpacing: 2 }}>{t.roomId}</span>
                                <button onClick={() => handleCopy(t.roomId!, 'roomId')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: copiedField === 'roomId' ? 'var(--success)' : 'var(--text-muted)', padding: 4, transition: 'color 0.2s' }}>
                                    {copiedField === 'roomId' ? <Check size={16} /> : <Copy size={16} />}
                                </button>
                            </div>
                        </div>
                        <div>
                            <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', fontWeight: 600, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 6 }}>Password</div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                <span style={{ fontFamily: 'monospace', fontSize: '1.3rem', fontWeight: 800, color: 'var(--success)', letterSpacing: 2 }}>{t.roomPassword as React.ReactNode}</span>
                                <button onClick={() => handleCopy(t.roomPassword as string, 'roomPassword')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: copiedField === 'roomPassword' ? 'var(--success)' : 'var(--text-muted)', padding: 4, transition: 'color 0.2s' }}>
                                    {copiedField === 'roomPassword' ? <Check size={16} /> : <Copy size={16} />}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {Boolean(t.status === 'live' && (!t.roomId || !t.roomPassword)) && (
                <div className="card animate-in stagger-3" style={{
                    marginBottom: 20, textAlign: 'center', padding: '20px 24px',
                    background: 'rgba(148,163,184,0.04)', border: '1px solid var(--border)',
                }}>
                    <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>⏳ Match is live — Room details will be available shortly. Stay tuned!</p>
                </div>
            )}

            {/* Prize Breakdown */}
            <div className="card animate-in stagger-3" style={{ marginBottom: 20 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 16 }}>
                    <h3 style={{ fontFamily: 'Outfit', fontWeight: 700, margin: 0 }}>🏆 Prize Breakdown</h3>
                    <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>Updates dynamically with slots</span>
                </div>
                {(t.prizes as any[] || []).map((p: any, i: number) => {
                    const currentGross = t.slotFill * t.entryFee;
                    const maxGross = t.maxSlots * t.entryFee;
                    
                    let dynamicCurrent = 0;
                    let dynamicMax = 0;
                    
                    if (t.game === 'PUBG' || t.game === 'Free Fire') {
                        const pcts = [0.25, 0.20, 0.15];
                        dynamicCurrent = Math.round(currentGross * (pcts[i] || 0));
                        dynamicMax = Math.round(maxGross * (pcts[i] || 0));
                    } else {
                        const pct = maxGross > 0 ? p.amount / maxGross : 0;
                        dynamicCurrent = Math.round(currentGross * pct);
                        dynamicMax = p.amount;
                    }

                    return (
                        <div key={i} style={{
                            display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px',
                            borderRadius: 'var(--radius-md)', marginBottom: 6,
                            background: i === 0 ? 'rgba(245,158,11,0.06)' : i === 1 ? 'rgba(148,163,184,0.04)' : 'transparent',
                            border: '1px solid var(--border)',
                        }}>
                            <span style={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8 }}>
                                {i === 0 ? '🥇' : i === 1 ? '🥈' : '🥉'} {p.rank} Place
                            </span>
                            <div style={{ textAlign: 'right' }}>
                                <div className="detail-val" style={{ color: 'var(--accent)', fontSize: '1.2rem' }}>
                                    <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}><TCoinIcon size={18} /> {dynamicCurrent.toLocaleString()}</span>
                                    <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontWeight: 500, marginTop: 4 }}>
                                        <span style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>Est. Max: <TCoinIcon size={10} /> {dynamicMax.toLocaleString()}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    );
                })}
                {(t.game === 'PUBG' || t.game === 'Free Fire') && (
                    <div style={{
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', marginTop: 6,
                        borderRadius: 'var(--radius-md)', background: 'rgba(56,189,248,0.05)', border: '1px solid var(--border)',
                    }}>
                        <span style={{ fontWeight: 600 }}>💀 Per Kill Bonus <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontWeight: 400 }}>(Top 10)</span></span>
                        <div style={{ textAlign: 'right' }}>
                            <div className="detail-item" style={{ flex: '1 1 30%' }}>
                                <div className="detail-val" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <TCoinIcon size={14} /> {Math.round(t.entryFee * 0.10)} <span style={{ fontSize: '0.8rem', fontWeight: 500 }}>/ Kill</span>
                                </div>
                            </div>
                            <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', display: 'block', marginTop: 2 }}>
                                Fixed 10% of Entry Fee
                            </span>
                        </div>
                    </div>
                )}
            </div>

            {/* Game Rules (from Firestore) */}
            <GameRulesCard game={t.game} />

            {/* Join Button */}
            { (t.status === 'registering' || t.status === 'open') && !hasJoined && (
                <div className="animate-in stagger-5" style={{ textAlign: 'center' }}>
                    <button className="btn btn-primary btn-lg" style={{ minWidth: 280, fontSize: '1.05rem', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px', margin: '0 auto' }} onClick={handleJoinClick}>
                        ⚡ Join Tournament —
                        <span style={{ display: 'flex', alignItems: 'center', gap: '3px', fontSize: '1.25rem', fontWeight: 800 }}>
                            <TCoinIcon size={24} />{t.entryFee}
                        </span>
                    </button>
                    <p style={{ marginTop: 8, fontSize: '0.78rem', color: 'var(--text-muted)', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '4px' }}>
                        <TCoinIcon size={14} />{t.entryFee} will be deducted from your wallet balance
                    </p>
                </div>
            )}
            { (t.status === 'registration closed' || t.status === 'upcoming') && !hasJoined && (
                <div className="animate-in stagger-5" style={{ textAlign: 'center' }}>
                    <button className="btn btn-primary btn-lg" style={{ minWidth: 280, fontSize: '1.05rem', background: 'var(--bg-glass)', borderColor: 'var(--border)', color: 'var(--error)', opacity: 0.8, pointerEvents: 'none' }} disabled>
                        Registrations Closed
                    </button>
                    <p style={{ marginTop: 8, fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                        This tournament is no longer accepting new participants.
                    </p>
                </div>
            )}
            { (t.status === 'registering' || t.status === 'registration closed' || t.status === 'open' || t.status === 'upcoming') && hasJoined && (
                <div className="animate-in stagger-5" style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
                    <button className="btn btn-primary btn-lg" style={{ minWidth: 280, fontSize: '1.05rem', background: 'var(--success)', borderColor: 'var(--success)', pointerEvents: 'none' }} disabled>
                        ✓ Registered
                    </button>
                    {canCancel && (t.status === 'registering' || t.status === 'open') && (
                        <button 
                            className="btn btn-primary" 
                            style={{ background: 'var(--danger)', borderColor: 'var(--danger)', color: 'white' }}
                            onClick={() => {
                                requireUnlock(() => {
                                    setJoinState('confirm-cancel');
                                    setShowModal(true);
                                });
                            }}
                        >
                            Cancel Registration
                        </button>
                    )}
                    {(!canCancel || t.status === 'registration closed' || t.status === 'upcoming') && (
                        <p style={{ marginTop: 4, fontSize: '0.85rem', color: 'var(--text-muted)' }}>You have successfully secured your slot! Registrations are locked in.</p>
                    )}
                </div>
            )}

            </motion.div>
            )}

            {/* ── Players Tab (Solo BR) ── */}
            {activeTab === 'players' && isSolo && (
                <motion.div key="players" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}>
                    <div className="card" style={{ padding: '20px' }}>
                        <h3 style={{ fontFamily: 'Outfit', fontWeight: 700, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
                            🎮 Registered Players
                            <span style={{
                                fontSize: '0.75rem', fontWeight: 600, padding: '2px 10px', borderRadius: 20,
                                background: 'rgba(99,102,241,0.08)', color: 'var(--accent-hover)',
                            }}>
                                {liveEntries.length || t.slotFill}
                            </span>
                        </h3>
                        {liveEntries.length > 0 && (
                            <div style={{ marginBottom: 16, padding: '12px 16px', borderRadius: 8, background: 'rgba(99,102,241,0.08)', border: '1px solid rgba(99,102,241,0.2)' }}>
                                <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                                    <div style={{ color: 'var(--accent)', fontSize: '1.2rem', lineHeight: 1 }}>📌</div>
                                    <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                                        <strong style={{ color: 'var(--accent)' }}>Important:</strong> The number next to your registration (e.g. #1, #2) is your assigned <strong>Lobby Slot Number</strong>. Please strictly occupy this exact slot in the custom room.
                                    </div>
                                </div>
                            </div>
                        )}
                        {liveEntries.length > 0 ? (
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 8 }}>
                                {liveEntries.map((entry, idx) => (
                                    <BRPlayerCard key={entry.id} entry={entry} index={idx} />
                                ))}
                            </div>
                        ) : (
                            <div style={{
                                textAlign: 'center', padding: '32px 20px',
                                background: 'rgba(148,163,184,0.03)', borderRadius: 10,
                                border: '1px dashed rgba(148,163,184,0.2)',
                            }}>
                                <div style={{ fontSize: '2rem', marginBottom: 8 }}>🎮</div>
                                <div style={{ fontWeight: 700, fontSize: '0.92rem', marginBottom: 4 }}>No Players Yet</div>
                                <div style={{ fontSize: '0.82rem', color: 'var(--text-muted)' }}>
                                    Players will appear here in real-time as they register.
                                </div>
                            </div>
                        )}
                    </div>
                </motion.div>
            )}

            {/* ── Teams Tab (Squad BR) ── */}
            {activeTab === 'teams' && isSquad && (
                <motion.div key="teams" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}>
                    <div className="card" style={{ padding: '20px' }}>
                        <h3 style={{ fontFamily: 'Outfit', fontWeight: 700, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
                            👥 Registered Squads
                            <span style={{
                                fontSize: '0.75rem', fontWeight: 600, padding: '2px 10px', borderRadius: 20,
                                background: 'rgba(99,102,241,0.08)', color: 'var(--accent-hover)',
                            }}>
                                {(() => {
                                    const squads = new Set(liveEntries.map(e => e.squadName || e.squadId).filter(Boolean));
                                    return squads.size || Math.ceil(t.slotFill / 4);
                                })()}
                            </span>
                        </h3>
                        {liveEntries.length > 0 && (
                            <div style={{ marginBottom: 16, padding: '12px 16px', borderRadius: 8, background: 'rgba(99,102,241,0.08)', border: '1px solid rgba(99,102,241,0.2)' }}>
                                <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                                    <div style={{ color: 'var(--accent)', fontSize: '1.2rem', lineHeight: 1 }}>📌</div>
                                    <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                                        <strong style={{ color: 'var(--accent)' }}>Important:</strong> The number next to your squad (e.g. #1, #2) is your assigned <strong>Lobby Slot Number</strong>. Please strictly occupy this exact slot in the custom room to avoid disqualification.
                                    </div>
                                </div>
                            </div>
                        )}
                        {liveEntries.length > 0 ? (
                            <BRSquadList entries={liveEntries} />
                        ) : (
                            <div style={{
                                textAlign: 'center', padding: '32px 20px',
                                background: 'rgba(148,163,184,0.03)', borderRadius: 10,
                                border: '1px dashed rgba(148,163,184,0.2)',
                            }}>
                                <div style={{ fontSize: '2rem', marginBottom: 8 }}>👥</div>
                                <div style={{ fontWeight: 700, fontSize: '0.92rem', marginBottom: 4 }}>No Squads Yet</div>
                                <div style={{ fontSize: '0.82rem', color: 'var(--text-muted)' }}>
                                    Squads will appear here in real-time as they register.
                                </div>
                            </div>
                        )}
                    </div>
                </motion.div>
            )}
            </AnimatePresence>


            {/* Join Modal */}
            {showModal && (
                <div style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    zIndex: 1000, padding: 20
                }}>
                    <div className="card animate-in" style={{ maxWidth: 400, width: '100%', textAlign: 'center', padding: 32 }}>
                        {joinState === 'confirm-cancel' ? (
                            <>
                                <h3 style={{ fontFamily: 'Outfit', fontWeight: 800, fontSize: '1.4rem', marginBottom: 16 }}>Cancel Registration?</h3>
                                <p style={{ color: 'var(--text-secondary)', marginBottom: 24, lineHeight: 1.5 }}>
                                    Are you sure you want to cancel your registration for <strong>{t.title}</strong>? 
                                    Your entry fee of <TCoinIcon size={12} /> {t.entryFee} will be refunded to your wallet.
                                </p>
                                <div style={{ display: 'flex', gap: 12 }}>
                                    <button className="btn btn-primary" style={{ flex: 1, background: 'var(--success)', borderColor: 'var(--success)', color: 'white' }} onClick={() => setShowModal(false)}>Keep Registration</button>
                                    <button className="btn btn-primary" style={{ flex: 1, background: 'var(--danger)', borderColor: 'var(--danger)', color: 'white' }} onClick={handleConfirmCancel} disabled={canceling}>{canceling ? 'Canceling...' : 'Yes, Cancel'}</button>
                                </div>
                            </>
                        ) : joinState === 'canceled' ? (
                            <>
                                <div style={{ width: 64, height: 64, borderRadius: '50%', background: 'rgba(239,68,68,0.1)', color: 'var(--error)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
                                    <XCircle size={32} />
                                </div>
                                <h3 style={{ fontFamily: 'Outfit', fontWeight: 800, fontSize: '1.4rem', marginBottom: 12 }}>
                                    Tournament Cancelled
                                </h3>
                                <p style={{ color: 'var(--text-secondary)', marginBottom: 24, lineHeight: 1.5, display: 'flex', flexWrap: 'wrap', justifyContent: 'center', alignItems: 'center', gap: '4px' }}>
                                    You have left <strong>{t.title}</strong>. Your entry fee of <TCoinIcon size={12} /> {t.entryFee} has been refunded to your wallet.
                                </p>
                                <button className="btn btn-primary" style={{ width: '100%' }} onClick={() => setShowModal(false)}>Close</button>
                            </>
                        ) : joinState === 'confirm' ? (
                            <>
                                <h3 style={{ fontFamily: 'Outfit', fontWeight: 800, fontSize: '1.4rem', marginBottom: 16 }}>Confirm Registration</h3>
                                <p style={{ color: 'var(--text-secondary)', marginBottom: 24, lineHeight: 1.5 }}>
                                    Are you sure you want to join <strong>{t.title}</strong>? 
                                    This action will reserve your slot for the tournament.
                                </p>
                                
                                <div style={{ background: 'var(--bg-glass)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', padding: 16, marginBottom: 24 }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, fontSize: '0.9rem' }}>
                                        <span style={{ color: 'var(--text-muted)' }}>Current Balance</span>
                                        <span style={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: '4px' }}><TCoinIcon size={14} /> {walletBalance.toLocaleString()}</span>
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12, fontSize: '0.9rem' }}>
                                        <span style={{ color: 'var(--text-muted)' }}>Joining Fee</span>
                                        <span style={{ fontWeight: 600, color: 'var(--warning)', display: 'flex', alignItems: 'center', gap: '4px' }}>-<TCoinIcon size={14} /> {t.entryFee}</span>
                                    </div>
                                    <div style={{ borderTop: '1px solid var(--border)', paddingTop: 12, display: 'flex', justifyContent: 'space-between' }}>
                                        <span style={{ fontWeight: 600 }}>Remaining Balance</span>
                                        <span style={{ fontWeight: 700, color: 'var(--success)', display: 'flex', alignItems: 'center', gap: '4px' }}><TCoinIcon size={14} /> {(walletBalance - t.entryFee).toLocaleString()}</span>
                                    </div>
                                </div>

                                <div style={{ display: 'flex', gap: 12 }}>
                                    <button className="btn btn-secondary" style={{ flex: 1 }} onClick={() => setShowModal(false)}>No, Cancel</button>
                                    <button className="btn btn-primary" style={{ flex: 1 }} onClick={handleConfirmJoin} disabled={joining}>{joining ? 'Joining...' : 'Yes, Join Now'}</button>
                                </div>
                            </>
                        ) : (
                            <>
                                <div style={{ width: 64, height: 64, borderRadius: '50%', background: 'rgba(16,185,129,0.1)', color: 'var(--success)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
                                    <Trophy size={32} />
                                </div>
                                <h3 style={{ fontFamily: 'Outfit', fontWeight: 800, fontSize: '1.4rem', marginBottom: 12 }}>
                                    Joined {t.title}!
                                </h3>
                                <p style={{ color: 'var(--text-secondary)', marginBottom: 24, lineHeight: 1.5 }}>
                                    Good luck, <strong>{user?.displayName || 'Player'}</strong>! Your entry fee has been securely locked until the tournament ends.
                                </p>
                                <button className="btn btn-primary" style={{ width: '100%' }} onClick={() => setShowModal(false)}>Back to Tournament</button>
                            </>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

// ─── Registration Countdown Banner ──────────────────────────────────────────

function RegistrationCountdownBanner({ closeTime }: { closeTime: string }) {
    const calcRemaining = useCallback(() => {
        return Math.max(0, new Date(closeTime).getTime() - Date.now());
    }, [closeTime]);

    const [remaining, setRemaining] = useState(calcRemaining);

    useEffect(() => {
        const timer = setInterval(() => {
            const r = calcRemaining();
            setRemaining(r);
            if (r <= 0) clearInterval(timer);
        }, 1000);
        return () => clearInterval(timer);
    }, [calcRemaining]);

    if (remaining <= 0) {
        return (
            <div style={{
                padding: '10px 16px', borderRadius: 8, marginBottom: 4,
                background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.2)',
                display: 'flex', alignItems: 'center', gap: 8,
                fontSize: '0.85rem', fontWeight: 700, color: '#ef4444',
            }}>
                🔒 Registration Closed
            </div>
        );
    }

    const hours = Math.floor(remaining / 3600000);
    const mins = Math.floor((remaining % 3600000) / 60000);
    const secs = Math.floor((remaining % 60000) / 1000);
    const pad = (n: number) => n.toString().padStart(2, '0');

    return (
        <div style={{
            padding: '10px 16px', borderRadius: 8, marginBottom: 4,
            background: 'rgba(99,102,241,0.05)', border: '1px solid rgba(99,102,241,0.15)',
            display: 'flex', alignItems: 'center', gap: 8,
        }}>
            <span style={{ fontSize: '0.85rem', opacity: 0.8 }}>⏱</span>
            <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>Registration closes in</span>
            <span style={{
                fontFamily: 'monospace', fontSize: '1rem', fontWeight: 800,
                color: 'var(--accent-hover)', letterSpacing: 1,
            }}>
                {pad(hours)}:{pad(mins)}:{pad(secs)}
            </span>
        </div>
    );
}

// ─── BR Player Card (Solo Mode) ─────────────────────────────────────────────

import type { TournamentEntry } from '../services/tournamentService';

function BRPlayerCard({ entry, index }: { entry: TournamentEntry; index: number }) {
    // Generate consistent hue from username
    const hue = entry.username.split('').reduce((acc, c) => acc + c.charCodeAt(0), 0) % 360;
    const avatarColor = `hsl(${hue}, 60%, 50%)`;

    return (
        <motion.div
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.03 }}
            style={{
                padding: '10px 14px', borderRadius: 10, display: 'flex', alignItems: 'center', gap: 12,
                background: 'rgba(148,163,184,0.04)', border: '1px solid var(--border)',
                transition: 'border-color 0.2s, background 0.2s',
            }}
        >
            {/* Avatar */}
            <AvatarDisplay
                avatar={entry.photoURL || null}
                username={entry.username}
                size={36}
                borderRadius={8}
                style={{ border: `2px solid ${avatarColor}40` }}
            />

            {/* Info */}
            <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 700, fontSize: '0.88rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {entry.username}
                </div>
                {entry.ign && (
                    <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 4 }}>
                        🎮 {entry.ign}
                    </div>
                )}
            </div>

            {/* Rank Number */}
            <div style={{
                fontSize: '0.7rem', fontWeight: 700, color: 'var(--text-muted)',
                padding: '2px 8px', borderRadius: 6, background: 'rgba(148,163,184,0.06)',
            }}>
                #{index + 1}
            </div>
        </motion.div>
    );
}

// ─── BR Squad List (Squad Mode) ─────────────────────────────────────────────

function BRSquadList({ entries }: { entries: TournamentEntry[] }) {
    // Group entries by squadName (or squadId fallback)
    const squadMap = new Map<string, TournamentEntry[]>();
    entries.forEach(e => {
        const key = e.squadName || e.squadId || 'Unknown Squad';
        if (!squadMap.has(key)) squadMap.set(key, []);
        squadMap.get(key)!.push(e);
    });

    const squads = Array.from(squadMap.entries());

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {squads.map(([squadName, members], idx) => (
                <BRSquadCard key={squadName} squadName={squadName} members={members} index={idx} />
            ))}
        </div>
    );
}

// ─── BR Squad Card (Expandable) ─────────────────────────────────────────────

function BRSquadCard({ squadName, members, index }: { squadName: string; members: TournamentEntry[]; index: number }) {
    const [expanded, setExpanded] = useState(false);

    // Generate color from squad name
    const hue = squadName.split('').reduce((acc, c) => acc + c.charCodeAt(0), 0) % 360;
    const color = `hsl(${hue}, 60%, 50%)`;
    const tag = squadName.split(' ').map(w => w[0]).join('').slice(0, 3).toUpperCase();

    // Split into main members (first 4) and substitute (5th)
    const mainMembers = members.slice(0, 4);
    const substitute = members.length > 4 ? members[4] : null;

    return (
        <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.05 }}
            className="card"
            style={{
                padding: '14px 16px', cursor: 'pointer', overflow: 'hidden',
                position: 'relative', transition: 'border-color 0.2s',
            }}
            onClick={() => setExpanded(!expanded)}
        >
            {/* Color bar */}
            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: `${color}80` }} />

            <div style={{ display: 'flex', alignItems: 'center', gap: 10, paddingTop: 2 }}>
                <div style={{
                    width: 36, height: 36, borderRadius: 8,
                    background: `${color}15`, border: `2px solid ${color}40`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '0.6rem', fontWeight: 900, color: color, letterSpacing: 0.5,
                }}>
                    {tag}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: '0.9rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {squadName}
                    </div>
                    <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>
                        {members.length} member{members.length !== 1 ? 's' : ''}
                    </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{
                        fontSize: '0.68rem', fontWeight: 700, padding: '2px 8px', borderRadius: 6,
                        background: 'rgba(99,102,241,0.08)', color: 'var(--accent-hover)',
                    }}>
                        #{index + 1}
                    </span>
                    {expanded ? <ChevronUp size={14} style={{ color: 'var(--text-muted)' }} /> : <ChevronDown size={14} style={{ color: 'var(--text-muted)' }} />}
                </div>
            </div>

            <AnimatePresence>
                {expanded && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        style={{ overflow: 'hidden', marginTop: 10 }}
                    >
                        <div style={{ padding: '10px 12px', background: 'rgba(148,163,184,0.03)', borderRadius: 8, border: '1px solid var(--border)' }}>
                            {/* Roster Header */}
                            <div style={{ fontSize: '0.7rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>
                                Main Roster ({mainMembers.length})
                            </div>

                            {/* Main Players */}
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                {mainMembers.map((member, i) => {
                                    const memberHue = member.username.split('').reduce((acc, c) => acc + c.charCodeAt(0), 0) % 360;
                                    const memberColor = `hsl(${memberHue}, 60%, 50%)`;
                                    return (
                                        <div key={member.id} style={{
                                            display: 'flex', alignItems: 'center', gap: 8,
                                            padding: '5px 8px', borderRadius: 6,
                                            background: i === 0 ? 'rgba(245,158,11,0.06)' : 'transparent',
                                            border: i === 0 ? '1px solid rgba(245,158,11,0.15)' : '1px solid transparent',
                                        }}>
                                            {/* Avatar */}
                                            <AvatarDisplay
                                                avatar={member.photoURL || null}
                                                username={member.username}
                                                size={22}
                                                borderRadius={5}
                                                style={{ border: `1px solid ${memberColor}30` }}
                                            />

                                            {/* Player Label */}
                                            <span style={{
                                                fontSize: '0.62rem', fontWeight: 700, width: 30,
                                                color: i === 0 ? 'var(--warning)' : 'var(--text-muted)',
                                                textTransform: 'uppercase', letterSpacing: 0.3,
                                            }}>
                                                {i === 0 ? 'LDR' : `P${i + 1}`}
                                            </span>

                                            {/* Username */}
                                            <span style={{
                                                flex: 1, fontSize: '0.78rem', fontWeight: i === 0 ? 700 : 500,
                                                color: i === 0 ? 'var(--text-primary)' : 'var(--text-secondary)',
                                            }}>
                                                {member.username}
                                            </span>

                                            {/* IGN */}
                                            {member.ign && (
                                                <span style={{ fontSize: '0.68rem', color: 'var(--text-muted)' }}>
                                                    🎮 {member.ign}
                                                </span>
                                            )}
                                        </div>
                                    );
                                })}

                                {/* Substitute */}
                                {substitute && (
                                    <>
                                        <div style={{ height: 1, background: 'rgba(255,255,255,0.05)', margin: '4px 0' }} />
                                        <div style={{
                                            display: 'flex', alignItems: 'center', gap: 8,
                                            padding: '5px 8px', borderRadius: 6,
                                        }}>
                                            <AvatarDisplay
                                                avatar={substitute.photoURL || null}
                                                username={substitute.username}
                                                size={22}
                                                borderRadius={5}
                                                style={{ border: '1px solid rgba(148,163,184,0.2)' }}
                                            />
                                            <span style={{ fontSize: '0.62rem', fontWeight: 700, width: 30, color: 'var(--text-secondary)', textTransform: 'uppercase' }}>
                                                SUB
                                            </span>
                                            <span style={{ flex: 1, fontSize: '0.78rem', fontWeight: 500, color: 'var(--text-secondary)' }}>
                                                {substitute.username}
                                            </span>
                                            {substitute.ign && (
                                                <span style={{ fontSize: '0.68rem', color: 'var(--text-muted)' }}>
                                                    🎮 {substitute.ign}
                                                </span>
                                            )}
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.div>
    );
}
